import { CircularProgress, Tooltip, useTheme } from "@mui/material";
import { LuBot, LuSparkle, LuUser } from "react-icons/lu";
import cn from "../../utils/cn";
import { INTERVIEWER_CHOICES } from "../../utils/aiInterviewerChoice";
import AIEnableFailures from "./AIEnableFailures";

const CHOICE_ICONS = { panelist: LuUser, ai_interviewer: LuBot, panelist_with_ai: LuSparkle };

// "Interviewer" on the schedule and edit forms: Panelist, AI interviewer, or Panelist + AI assistant.
// `options` comes from useAIInterviewSetting: { [value]: { disabled, reason } }.
// A compact row like the meet type picker; only the selected option's description is shown.
export default function AIInterviewerChoice({
  label = "Interviewer",
  value,
  onChange,
  options = {},
  disabled,
  loading,
  error,
  selectionError,
  failures = [],
  onRetry,
  retrying,
}) {
  const theme = useTheme();
  const muted = { color: theme.palette.text.secondary || "#6b7280" };
  const selectedChoice = INTERVIEWER_CHOICES.find((choice) => choice.value === value);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <p id="interviewer-choice-label" className="text-sm font-medium">{label}</p>
        {loading && <CircularProgress size={14} aria-label="Loading AI interviewer status" />}
      </div>
      <div role="radiogroup" aria-labelledby="interviewer-choice-label" className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {INTERVIEWER_CHOICES.map((choice) => {
          const state = options[choice.value] || {};
          const optionDisabled = disabled || loading || state.disabled;
          const selected = value === choice.value;
          const Icon = CHOICE_ICONS[choice.value];
          const card = (
            <label
              className={cn(
                "flex h-full min-h-[48px] items-center gap-2.5 rounded-lg border px-3.5 py-2 text-sm transition-colors focus-within:outline focus-within:outline-2 focus-within:outline-offset-2",
                selected ? "font-semibold" : "font-medium",
                optionDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
              )}
              style={{
                borderColor: selected ? theme.palette.info.main : theme.palette.secondary.v4,
                backgroundColor: selected ? "#E8EEF8" : "#ffffff",
                color: selected ? theme.palette.info.main : "#111111",
                boxShadow: selected ? `inset 0 0 0 1px ${theme.palette.info.main}` : "none",
                outlineColor: theme.palette.info.main,
              }}
            >
              <input
                type="radio"
                name="interviewer-choice"
                value={choice.value}
                checked={selected}
                disabled={optionDisabled}
                onChange={() => onChange(choice.value)}
                className="sr-only"
                aria-describedby={state.reason ? `interviewer-reason-${choice.value}` : undefined}
              />
              {Icon && <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} fill={choice.value === "panelist_with_ai" ? "currentColor" : "none"} aria-hidden="true" />}
              <span className="leading-tight">{choice.label}</span>
              {state.reason && (
                <span id={`interviewer-reason-${choice.value}`} className="sr-only">{state.reason}</span>
              )}
            </label>
          );
          return state.reason ? (
            <Tooltip key={choice.value} title={state.reason} arrow>
              {/* A wrapper so the tooltip still opens on a disabled option */}
              <span className="block">{card}</span>
            </Tooltip>
          ) : (
            <div key={choice.value}>{card}</div>
          );
        })}
      </div>
      {selectedChoice && <p className="text-xs" style={muted}>{selectedChoice.description}</p>}
      {selectionError && <p className="text-sm text-red-600" role="alert">{selectionError}</p>}
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      <AIEnableFailures failures={failures} onRetry={onRetry} retrying={retrying} />
    </div>
  );
}
